import Uppy from '@uppy/core';
import XHR from '@uppy/xhr-upload';
import { TinyEmitter } from 'tiny-emitter';
import { vidTranslation } from '@/api';

const FILE_SLICE_SIZE = 5 * 1024 * 1024;

function sliceFile(file) {
  const chunks = [];
  let pointer = 0;
  while (pointer < file.size) {
    chunks.push({
      eTag: '',
      blob: file.slice(pointer, (pointer += FILE_SLICE_SIZE)),
    });
  }
  return chunks;
}

function buildUsage(usageType, options) {
  let usage = { type: usageType };
  if (usageType === 0) {
    //project
    usage = { ...usage, ext: options['ext'] };
  } else if (usageType === 1) {
    // thumbnail
    // do nothing
  } else if (usageType === 2) {
    // avatar
    usage = { ...usage, ext: options['ext'] };
  } else if (usageType === 3) {
    // voice_clone
    // do nothing
    usage = { ...usage, ext: options['ext'] };
  } else if (usageType === 4) {
    // asset
    usage = { ...usage, filename: options['filename'] };
  } else if (usageType === 9) {
    // asset
    usage = { ...usage, ext: options['ext'] };
  }
  return usage;
}

export default class Uploader extends TinyEmitter {
  #usage;
  #file;
  chunks = [];
  #successCount = 0;
  #uploadCount = 0;
  #uploadId;
  #uppy;
  onComplete;

  constructor(usage, options) {
    super();
    this.#usage = buildUsage(usage, options);
    this.#uppy = new Uppy().use(XHR, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/octet-stream',
      },
      formData: false,
      onBeforeRequest: (xhr, retryCount, files) => {
        xhr.index = files[0].meta.index;
        this.#uppy.getPlugin('XHRUpload').setOptions({
          endpoint: files[0].meta.url,
        });
      },
      onAfterResponse: (xhr) => {
        this.chunks[xhr.index].eTag = xhr.getResponseHeader('ETag');
      },
      getResponseData(responseText, response) {
        // 假设后端只返回了一个 URL 字符串
        return {
          url: '',
        };
      },
    });
    this.#bindEvents();
  }

  #bindEvents() {
    this.#uppy.on('complete', ({ successful }) => {
      this.#uploadCount++;
      this.#successCount += successful.length;
      this.emit('progress', this.#successCount / this.chunks.length);

      if (this.#successCount === this.chunks.length) {
        this.emit('success', this.generateSuccessResult());
      } else if (this.#uploadCount === this.chunks.length) {
        this.emit('failed');
      }
    });
  }

  generateSuccessResult() {
    const result = {
      uploadId: this.#uploadId,
      eTags: this.chunks.map((chunk) => ({
        partNumber: chunk.partNumber,
        eTag: chunk.eTag,
      })),
    };
    this.onComplete(result);
    return result;
  }

  uploadFile(file) {
    this.#file = file;
    this.chunks = [
      {
        eTag: '',
        blob: file,
      },
    ];
    this.#createUpload();
  }

  uploadFileWithSlice(file) {
    this.#file = file;
    this.chunks = sliceFile(file);
    this.#createUpload();
  }

  uploadChunks(urls) {
    urls.forEach((url, index) => {
      this.chunks[index].partNumber = index + 1;

      if (index === 0) {
        this.#uppy.getPlugin('XHRUpload').setOptions({
          endpoint: url,
        });
      }

      this.#uppy.addFile({
        name: `chunk-${index}`,
        data: this.chunks[index].blob,
        meta: {
          index,
          url: urls[index + 1],
        },
      });

      this.#uppy.upload();
    });
  }

  #createUpload() {
    vidTranslation
      .newUpload({
        usage: this.#usage,
        fileExtension: this.#file.name.replace(/.*\./g, ''),
        assetSize: this.#file.size,
        parts: this.chunks.length,
      })
      .then((res) => {
        if (res?.code !== 0) {
          console.error('create new upload task failed');
          throw new Error('create new upload task failed');
        } else {
          const { uploadId, urls } = res.data;
          this.#uploadId = uploadId;
          this.uploadChunks(urls);
        }
      });
  }

  retry() {
    this.#uppy.retryAll().then(({ failed }) => {
      if (failed.length > 0) {
        this.emit('retry-failed');
      }
    });
  }

  stop() {
    this.#uppy.cancelAll();
  }
}

export class VideoProjectUploader extends Uploader {
  #video;
  #videoId;
  #thumbnail;
  #thumbnailId;

  constructor(video, thumbnail) {
    super(0);
    this.#video = video;
    this.#thumbnail = thumbnail;
  }

  upload() {
    this.chunks = sliceFile(this.#video);
    vidTranslation
      .multiUploadTask({
        streams: [
          {
            ext: 'mp4',
            mimeType: 'video/mp4',
          },
        ],
        parts: this.chunks.length,
        thumbnail: true,
      })
      .then((res) => {
        if (res?.code !== 0) {
          console.error('create project upload task failed');
          throw new Error('create project upload task failed');
        } else {
          const { uploadIds, contentUrls, thumbnailUrl } = res.data;
          this.#videoId = uploadIds[0];
          this.#thumbnailId = uploadIds[1];
          this.chunks.push({
            eTag: '',
            blob: this.#thumbnail,
          });
          contentUrls.push(thumbnailUrl);
          super.uploadChunks(contentUrls);
          this.chunks.at(-1).partNumber = 1;
        }
      });
  }

  generateSuccessResult() {
    const thumbnail = this.chunks.pop();
    return {
      stream: {
        uploadId: this.#videoId,
        eTags: this.chunks.map((chunk) => ({
          partNumber: chunk.partNumber,
          eTag: chunk.eTag,
        })),
      },
      thumbnail: {
        uploadId: this.#thumbnailId,
        eTags: [
          {
            partNumber: thumbnail.partNumber,
            eTag: thumbnail.eTag,
          },
        ],
      },
    };
  }
}
